import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const LangContext = createContext({ lang: "en", setLang: () => {}, toggle: () => {} });
const STORAGE_KEY = "ccc_lang";

/**
 * LangProvider — persists user's preferred language (en | es) in localStorage
 * so bilingual visitors don't retoggle on every navigation. Public content
 * calls pass ?lang=<current> to their fetch endpoints.
 */
export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return (stored === "es" || stored === "en") ? stored : "en";
    } catch { return "en"; }
  });

  const setLang = useCallback((next) => {
    if (next !== "en" && next !== "es") return;
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  const toggle = useCallback(() => setLang(lang === "en" ? "es" : "en"), [lang, setLang]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, toggle }), [lang, setLang, toggle]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
